import React from 'react';
import { motion } from 'framer-motion';
import useT from '../../../i18n';
import { useStories } from '../../../config/stories';

/**
 * Stories — Fase 2.5 addition (portert fra legacy SuccessStory).
 *
 * 3 av 6 real stories fra config/stories.js: Irris (helse/🇵🇭), Alfons
 * (industri/🇦🇱), Dila (oppvekst/🇦🇱) — bredde i sektor og nasjonalitet.
 * Klikk et kort → /historier/{id} for full artikkel.
 *
 * Design: editorial magazine-grid på bg-paper. Portrett-forward 4:5 crop,
 * flag-emoji + rolle som eyebrow i fjord-farge, Fraunces headline, Inter
 * teaser, tekst-lenke med subtle underline.
 */
export default function Stories({ onNavigate }) {
  const t = useT();
  const s = t.landingV2.stories;
  const allStories = useStories();
  // Curated selection for editorial breadth (sector + country + gender-balance)
  const CURATED_IDS = ['irris', 'alfons', 'dila'];
  const featured = CURATED_IDS
    .map((id) => allStories.find((x) => x.id === id))
    .filter(Boolean);

  return (
    <section
      className="bg-paper py-section border-t border-ink/[0.06]"
      data-testid="landing-stories"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <p className="font-sans text-xs uppercase tracking-widest text-ink/50">
            {s.eyebrow}
          </p>
          <h2 className="mt-6 font-display font-light text-display-md text-ink">
            {s.heading}
          </h2>
          {s.subCopy && (
            <p className="mt-6 font-sans text-base text-ink/70 max-w-prose-wide leading-relaxed">
              {s.subCopy}
            </p>
          )}
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10">
          {featured.map((story, i) => (
            <motion.button
              key={story.id}
              type="button"
              onClick={() => onNavigate?.(`/historier/${story.id}`)}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.1 + i * 0.1,
              }}
              className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-4 focus-visible:ring-offset-paper rounded-sm"
              data-testid={`story-card-${story.id}`}
            >
              <div className="overflow-hidden">
                <img
                  src={story.portrait}
                  alt={story.name}
                  className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  style={{ objectPosition: story.portraitPosition || 'center 30%' }}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="mt-6 font-sans text-xs uppercase tracking-widest text-fjord/70">
                <span className="mr-1.5" aria-hidden>{story.fromFlag}</span>
                {story.role}
              </p>
              <h3 className="mt-3 font-display text-xl text-ink leading-tight">
                {story.name}
              </h3>
              <p className="mt-2 font-sans text-sm text-ink/60 leading-relaxed">
                {story.teaser}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-sans text-sm text-ink underline underline-offset-4 decoration-ink/20 group-hover:decoration-ink transition-colors">
                {s.readMore} <span aria-hidden>→</span>
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
